/** The basket you build by clicking the chain (simulator S3).
 *
 * Positions live in the page and nowhere else. Persisting a simulation is
 * S4's job and needs a table; until then, closing the tab discards it —
 * which is the honest behaviour for something with no save button, and
 * far better than a half-persistence that survives a reload but not a
 * deploy.
 *
 * The ENTRY price is frozen at the minute a leg is added. That is the
 * whole point of stepping the clock afterwards: the entry stays put while
 * the market moves around it. Nothing here may re-read it.
 */

import { useCallback, useState } from "react";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { simulatorApi, type SimLeg, type SimMtm, type SimPayoff } from "@/lib/simulatorApi";

export interface PositionsState {
  /** The legs as BUILT, at their own lot counts. The multiplier is not
   * folded in here — the table shows these, so a ×3 basket still reads as
   * the 1:2 ratio spread it is. */
  legs: SimLeg[];
  /** What is actually priced: `legs` with every lot count multiplied. */
  scaled: SimLeg[];
  multiplier: number;
  /** The largest multiplier this basket can take before its biggest leg
   * would exceed the API's 100-lot cap. */
  maxMultiplier: number;
  setMultiplier: (n: number) => void;
  payoff: UseQueryResult<SimPayoff>;
  /** The realised path across the WHOLE session — one request, drawn
   * once, so auto-run costs no traffic. Deliberately not keyed on the
   * minute: the curve does not change as the clock moves, only the
   * marker on it does. */
  mtm: UseQueryResult<SimMtm>;
  add: (leg: SimLeg) => void;
  replaceAll: (legs: SimLeg[]) => void;
  setLots: (index: number, lots: number) => void;
  /** Correct a leg's fill price. */
  setEntry: (index: number, entry: number) => void;
  /** Book a leg at `price`, as of minute `at`. It stays in the basket. */
  closeAt: (index: number, at: string, price: number) => void;
  /** Book every still-open leg at this minute. */
  closeAll: (at: string, priceOf: (leg: SimLeg) => number | null) => void;
  /** Undo a close: the leg is open again, same entry, marking live. */
  reopen: (index: number) => void;
  /** Type a premium over the archive's for the minute `at` only. */
  setMark: (index: number, at: string, price: number) => void;
  /** Drop the typed premium: the archive's own price returns. */
  clearMark: (index: number) => void;
  /** Correct the price a closed leg was booked at. The minute stays. */
  setExit: (index: number, price: number) => void;
  removeAt: (index: number) => void;
  removeContract: (strike: number, side: SimLeg["side"]) => void;
  clear: () => void;
}

interface Context {
  underlying: string | null;
  tradeDate: string | null;
  at: string;
  expiry: string | null;
}

/** The API accepts 1..100 lots, so the UI must not offer more — a leg
 * outside that range fails the whole basket's pricing request, not just
 * its own row. */
export const MIN_LOTS = 1;
export const MAX_LOTS = 100;

/** ⭐ THE MULTIPLIER SCALES THE WHOLE BASKET, and is deliberately a
 * SEPARATE number rather than an edit to each leg's lots.
 *
 * Folding it into the legs would be lossy in both directions: a 1:2 ratio
 * spread taken to x3 and back would have to divide 3 and 6 by three and
 * hope, and any non-integral step would silently round the RATIO — which
 * is the one thing about a ratio spread that must not move. Kept apart,
 * the table keeps showing the structure as built and the multiplier is
 * exactly reversible.
 *
 * The ceiling is per-basket, not a constant: the biggest leg is what hits
 * the 100-lot cap first, so a 2-lot ratio leg caps the whole basket at
 * x50 while a straddle of 1-lot legs goes to x100. */
export function maxMultiplierFor(legs: SimLeg[]): number {
  const biggest = legs.reduce((m, l) => Math.max(m, l.lots), 0);
  if (biggest <= 0) return MAX_LOTS;
  return Math.max(1, Math.floor(MAX_LOTS / biggest));
}

export function scaleLegs(legs: SimLeg[], multiplier: number): SimLeg[] {
  if (multiplier === 1) return legs;
  return legs.map((l) => ({ ...l, lots: clampLots(l.lots * multiplier) }));
}

function clampLots(n: number): number {
  if (!Number.isFinite(n)) return MIN_LOTS;
  return Math.min(MAX_LOTS, Math.max(MIN_LOTS, Math.round(n)));
}

/** Lots held on each contract-and-direction, for the chain's badges.
 *
 * Keyed by strike+side+direction and SUMMED across minutes: the chain has
 * one row per strike, so it must report the whole holding there, not
 * whichever entry happened to be last. */
export function lotsByContract(legs: SimLeg[]): Map<string, number> {
  const out = new Map<string, number>();
  for (const l of legs) {
    const key = `${String(l.strike)}-${l.side}-${l.direction}`;
    out.set(key, (out.get(key) ?? 0) + l.lots);
  }
  return out;
}

/** ⭐ MERGED ONLY WITHIN THE SAME MINUTE. Two lots bought at different
 * minutes are two entries at different prices, and averaging them would
 * erase the thing being replayed — so those still append. But the SAME
 * contract clicked twice on the SAME minute is one position of two lots at
 * one price, and splitting it into two identical rows is noise that also
 * makes the total size harder to read. */
function merge(current: SimLeg[], leg: SimLeg): SimLeg[] {
  const i = current.findIndex(
    (l) =>
      l.strike === leg.strike &&
      l.side === leg.side &&
      l.direction === leg.direction &&
      l.entry_at === leg.entry_at,
  );
  if (i === -1) return [...current, leg];
  return current.map((l, j) => (j === i ? { ...l, lots: clampLots(l.lots + leg.lots) } : l));
}

function usePayoffQuery(ctx: Context, legs: SimLeg[], count: number): UseQueryResult<SimPayoff> {
  return useQuery({
    // The minute is part of the key: stepping the clock reprices the same
    // basket, which is exactly what the reader is stepping to see.
    queryKey: ["simulator-payoff", ctx.underlying, ctx.tradeDate, ctx.at, ctx.expiry, legs],
    queryFn: () =>
      simulatorApi.payoff({
        underlying: ctx.underlying ?? "",
        tradeDate: ctx.tradeDate ?? "",
        at: ctx.at,
        expiry: ctx.expiry ?? "",
        legs,
      }),
    enabled: count > 0 && ctx.underlying !== null && ctx.tradeDate !== null && ctx.expiry !== null,
  });
}

function withoutMarks(legs: SimLeg[]): SimLeg[] {
  return legs.map((l) => ({ ...l, mark_at: null, mark: null }));
}

function useMtmQuery(ctx: Context, legs: SimLeg[], count: number): UseQueryResult<SimMtm> {
  return useQuery({
    // Deliberately NOT keyed on the minute: the curve is the whole
    // session and does not change as the clock moves, only the marker on
    // it does.
    //
    // A typed LTP is one minute's price, so it is left out here: it would
    // refetch the whole session's curve for a number the curve ignores.
    queryKey: ["simulator-mtm", ctx.underlying, ctx.tradeDate, ctx.expiry, withoutMarks(legs)],
    queryFn: () =>
      simulatorApi.mtm({
        underlying: ctx.underlying ?? "",
        tradeDate: ctx.tradeDate ?? "",
        expiry: ctx.expiry ?? "",
        legs: withoutMarks(legs),
      }),
    enabled: count > 0 && ctx.underlying !== null && ctx.tradeDate !== null && ctx.expiry !== null,
  });
}

/** ⭐ EDITING A LEG COMMITS THE MULTIPLIER.
 *
 * The Lots column shows the EFFECTIVE size, so a typed 4 has to mean four
 * lots — not four times the multiplier. Folding the scaling into every leg
 * and resetting the multiplier to 1 is the only way the typed number means
 * what it says while every other leg keeps the size it was already
 * showing.
 *
 * It also removes the state the operator caught: Lots 1 beside Multiplier
 * 10 beside Qty 650, three numbers that never visibly agree. After any
 * hand edit there is one number per leg and nothing multiplying it.
 */
function useSetLots(
  setLegs: React.Dispatch<React.SetStateAction<SimLeg[]>>,
  setMultiplier: React.Dispatch<React.SetStateAction<number>>,
  multiplier: number,
): (index: number, lots: number) => void {
  return useCallback(
    (index: number, lots: number) => {
      setLegs((current) =>
        scaleLegs(current, multiplier).map((l, i) =>
          i === index ? { ...l, lots: clampLots(lots) } : l,
        ),
      );
      setMultiplier(1);
    },
    [setLegs, setMultiplier, multiplier],
  );
}

/** Closing books a leg where clearing removes it.
 *
 * Operator, 2026-08-29: *"need one more close position which will just
 * close the open position"*. The distinction is the whole point — a
 * cleared leg never happened, a closed one happened and is finished, and
 * only the second can show you what a replayed trade actually made.
 *
 * ⚠️ Already-closed legs are left alone by `closeAll`. Re-closing one at a
 * later minute would rewrite a result that is already booked, which is
 * exactly the thing closing is supposed to protect.
 */
function useClosing(
  setLegs: React.Dispatch<React.SetStateAction<SimLeg[]>>,
): Pick<PositionsState, "closeAt" | "closeAll"> {
  const closeAt = useCallback(
    (index: number, at: string, price: number) => {
      setLegs((current) =>
        current.map((l, i) => (i === index ? { ...l, exit_at: at, exit: price } : l)),
      );
    },
    [setLegs],
  );

  const closeAll = useCallback(
    (at: string, priceOf: (leg: SimLeg) => number | null) => {
      setLegs((current) =>
        current.map((l) => {
          if (l.exit_at != null) return l;
          const price = priceOf(l);
          // A leg the archive cannot price at this minute stays OPEN. A
          // guessed exit price is a fabricated result, and a booked number
          // is the one thing in here that must never be invented.
          return price === null ? l : { ...l, exit_at: at, exit: price };
        }),
      );
    },
    [setLegs],
  );

  return { closeAt, closeAll };
}

/** The edits a booked or marked leg can take: reopening it, typing its LTP,
 * and correcting the price it was booked at. */
function useLegEdits(
  setLegs: React.Dispatch<React.SetStateAction<SimLeg[]>>,
): Pick<PositionsState, "setEntry" | "reopen" | "setMark" | "clearMark" | "setExit"> {
  // ⭐ THE FILL PRICE IS EDITABLE. Clicking the chain records the premium
  // SHOWN at that minute, which is the honest default — but a real fill is
  // rarely the last traded price, and the point of replaying a session is
  // often to ask what a WORSE fill would have done to it. Nothing else
  // moves: the entry minute, the lots and any booked exit stay as they
  // were, so only the number being questioned changes.
  //
  // Negative premiums are impossible and the API rejects them, so the
  // floor is 0 rather than a validation error the panel cannot show.
  const setEntry = useCallback(
    (index: number, entry: number) => {
      const safe = Number.isFinite(entry) ? Math.max(0, entry) : 0;
      setLegs((current) => current.map((l, i) => (i === index ? { ...l, entry: safe } : l)));
    },
    [setLegs],
  );

  // Both fields go together or not at all (the API rejects one without the
  // other), and a response leg carries JSON null for an open one.
  const reopen = useCallback(
    (index: number) => {
      setLegs((current) =>
        current.map((l, i) => (i === index ? { ...l, exit_at: null, exit: null } : l)),
      );
    },
    [setLegs],
  );

  // ⭐ A TYPED LTP BELONGS TO ONE MINUTE. The server uses it only when the
  // request's minute is `mark_at`, so stepping the clock returns to the
  // archive's price instead of carrying one hand-typed number all session.
  const setMark = useCallback(
    (index: number, at: string, price: number) => {
      const safe = Number.isFinite(price) ? Math.max(0, price) : 0;
      setLegs((current) =>
        current.map((l, i) => (i === index ? { ...l, mark_at: at, mark: safe } : l)),
      );
    },
    [setLegs],
  );

  const clearMark = useCallback(
    (index: number) => {
      setLegs((current) =>
        current.map((l, i) => (i === index ? { ...l, mark_at: null, mark: null } : l)),
      );
    },
    [setLegs],
  );

  // A closed leg's shown price IS its booked price, so editing it edits the
  // exit. Same floor as the entry: a premium cannot be negative.
  const setExit = useCallback(
    (index: number, price: number) => {
      const safe = Number.isFinite(price) ? Math.max(0, price) : 0;
      setLegs((current) =>
        current.map((l, i) => (i === index && l.exit_at != null ? { ...l, exit: safe } : l)),
      );
    },
    [setLegs],
  );

  return { setEntry, reopen, setMark, clearMark, setExit };
}

export function usePositions(ctx: Context): PositionsState {
  const [legs, setLegs] = useState<SimLeg[]>([]);
  const [multiplier, setMultiplierRaw] = useState(1);

  const maxMultiplier = maxMultiplierFor(legs);
  // Editing a leg upward can shrink the ceiling under a multiplier already
  // set, so it is clamped on READ rather than only when typed. Otherwise a
  // x40 basket whose leg goes 2 -> 3 would quietly send 120 lots and fail
  // the whole pricing call.
  const effective = Math.min(Math.max(1, multiplier), maxMultiplier);
  const scaled = scaleLegs(legs, effective);

  const payoff = usePayoffQuery(ctx, scaled, legs.length);
  const mtm = useMtmQuery(ctx, scaled, legs.length);

  const setMultiplier = useCallback((n: number) => {
    setMultiplierRaw(Number.isFinite(n) ? Math.max(1, Math.round(n)) : 1);
  }, []);

  const replaceAll = useCallback((next: SimLeg[]) => {
    setLegs(next);
  }, []);

  const add = useCallback((leg: SimLeg) => {
    setLegs((current) => merge(current, leg));
  }, []);

  const setLots = useSetLots(setLegs, setMultiplierRaw, effective);

  const closing = useClosing(setLegs);
  const edits = useLegEdits(setLegs);

  const removeAt = useCallback((index: number) => {
    setLegs((current) => current.filter((_, i) => i !== index));
  }, []);

  // Removes EVERY leg on that contract and side, including ones entered at
  // different minutes. The chain shows one control per contract, so a
  // remove there has to mean the whole holding — leaving a stray leg from
  // an earlier minute behind, with the chain then showing nothing, would
  // be a position you can no longer see where you put it.
  const removeContract = useCallback((strike: number, side: SimLeg["side"]) => {
    setLegs((current) => current.filter((l) => !(l.strike === strike && l.side === side)));
  }, []);

  const clear = useCallback(() => {
    setLegs([]);
  }, []);

  return {
    legs,
    scaled,
    multiplier: effective,
    maxMultiplier,
    setMultiplier,
    payoff,
    mtm,
    add,
    replaceAll,
    setLots,
    ...closing,
    ...edits,
    removeAt,
    removeContract,
    clear,
  };
}
